<script setup lang="ts">
import { debounce } from 'lodash';
import { ref, onMounted, computed, watch } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import ConnxProgressBar from '@/components/Atoms/ConnxProgressBar.vue';
import { DashboardWidget } from '@/interfaces/Dashboard';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();

interface Serie {
  name: string;
  data: number;
}

const dashboardStore = useDashboardStore();
const loading = ref(true);
const threshold = ref(0);
const benchmark = ref(0);
const series = ref<Serie[]>([]);

const generateBarColor = (data: number) => {
  if (benchmark.value === 0) {
    return 'tw-bg-connx-primary';
  }
  if (data < benchmark.value) {
    return 'tw-bg-connx-success';
  }
  if (data <= benchmark.value + threshold.value) {
    return 'tw-bg-connx-warning';
  }

  return 'tw-bg-connx-danger';
};

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const fetchData = async () => {
  const data = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
    widgetGuid: props.widget.dashboardTabWidgetGuid,
  });

  if (data?.data !== undefined) {
    series.value = data.data;
  }

  if (data?.threshold !== undefined) {
    threshold.value = data.threshold;
  }

  if (data?.benchmark !== undefined) {
    benchmark.value = data.benchmark;
  }

  if (data?.series === undefined && data?.threshold === undefined && data?.benchmark === undefined) {
    series.value = [];
  }
};

onMounted(async () => {
  loading.value = true;
  await fetchData();
  loading.value = false;
});

const maxValue = computed(() => {
  return Math.max(...series.value.map((s) => s.data)) + 2;
});

const listData = computed(() => {
  return series.value.map((s) => ({
    ...s,
    color: generateBarColor(s.data),
    percentage: (s.data / maxValue.value) * 100,
  }));
});

watch(
  () => [props.widget?.defaultJsonProperties?.parameters],
  async () => {
    await debouncedFetchData();
  },
  { deep: true }
);
</script>

<template>
  <div class="ScrollBar ScrollBar--vertical tw-w-full" :style="{ height: `${height}px`, overflowY: 'auto' }">
    <div class="tw-w-full">
      <div class="tw-flex tw-items-center tw-justify-center" v-if="benchmark > 0">
        <p>{{ benchmark }}% Avg. Rate</p>
      </div>

      <div class="tw-grid tw-grid-cols-[max-content_minmax(auto,1fr)] tw-gap-4 tw-border tw-border-gray-300">
        <template v-for="(serie, index) in listData" :key="index">
          <div class="tw-flex tw-items-center">
            <p class="tw-mb-0 tw-leading-[1.2] tw-font-bold">{{ serie.name }}</p>
          </div>
          <div class="tw-bg-gray-200">
            <connx-progress-bar
              :average="(benchmark / maxValue) * 100"
              :percentage="serie.percentage"
              textClass="tw-leading-[1.4] tw-text-lg"
              text-color="tw-text-connx-white"
              :background-color="serie.color"
              background-fill="tw-bg-connx-grey-tint-70"
            >
              {{ serie.data }}%
            </connx-progress-bar>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
